export type InAppBrowserApp = "instagram" | "tiktok" | null;
export type MobilePlatform = "android" | "ios" | null;

const INSTAGRAM_UA_PATTERN = /Instagram/i;
const TIKTOK_UA_PATTERN = /musical_ly|TikTok/i;
const ANDROID_UA_PATTERN = /Android/i;
const IOS_UA_PATTERN = /iPhone|iPad|iPod/i;

/**
 * Detects whether the given User-Agent belongs to the Instagram or TikTok
 * in-app browser (IAB). These IABs are known to interfere with reCAPTCHA,
 * trackers and other browser features.
 */
export function detectInAppBrowser(userAgent: string): InAppBrowserApp {
  if (INSTAGRAM_UA_PATTERN.test(userAgent)) return "instagram";
  if (TIKTOK_UA_PATTERN.test(userAgent)) return "tiktok";

  return null;
}

export function detectMobilePlatform(userAgent: string): MobilePlatform {
  if (ANDROID_UA_PATTERN.test(userAgent)) return "android";
  if (IOS_UA_PATTERN.test(userAgent)) return "ios";

  return null;
}

/**
 * Builds an Android intent:// URL that forces the current page to open in
 * Chrome, bypassing the IAB. `browser_fallback_url` keeps the original URL
 * as a fallback if Chrome isn't available/allowed to handle it.
 */
export function buildAndroidIntentUrl(url: string): string {
  const parsed = new URL(url);
  const scheme = parsed.protocol.replace(":", "");
  const authorityAndPath = `${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`;

  return `intent://${authorityAndPath}#Intent;scheme=${scheme};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`;
}

/**
 * Prefixes the URL scheme with `x-safari-`, which on some iOS versions
 * forces the link to open in Safari instead of the IAB's WebView. Apple/IAB
 * vendors may block this at any time, so it must always be paired with a
 * manual fallback.
 */
export function buildIosExternalBrowserUrl(url: string): string {
  return url.replace(/^https?:\/\//i, scheme => `x-safari-${scheme}`);
}
